import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Download, FileText } from "lucide-react";

export default function Submissions() {
    const [pending, setPending] = useState([]);
    const [grading, setGrading] = useState(null);
    const [form, setForm] = useState({ score: 0, remarks: "", approved: true });

    const load = async () => {
        const { data } = await api.get("/dashboard/faculty");
        setPending(data.pending_submissions);
    };
    useEffect(() => { load(); }, []);

    const download = async (s) => {
        const resp = await api.get(`/submissions/${s.id}/download`, { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        const a = document.createElement("a");
        a.href = url; a.download = s.filename || "submission"; a.click();
        URL.revokeObjectURL(url);
    };

    const grade = async () => {
        if (!grading) return;
        await api.post(`/submissions/${grading.id}/grade`, form);
        toast.success("Submission graded");
        setGrading(null);
        load();
    };

    return (
        <div className="space-y-6" data-testid="submissions-page">
            <div>
                <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Grade</div>
                <h1 className="font-display text-3xl lg:text-4xl mt-1">Pending submissions</h1>
            </div>
            <div className="grid gap-3">
                {pending.map(s => (
                    <Card key={s.id} className="mx-card" data-testid={`submission-${s.id}`}>
                        <CardContent className="p-5 flex items-center gap-4">
                            <div className="h-11 w-11 rounded-xl bg-[#F0EFFF] dark:bg-[#1C1A2E] flex items-center justify-center"><FileText className="h-5 w-5 text-[#726AF0]" /></div>
                            <div className="flex-1">
                                <div className="font-bold">{s.assignment_title}</div>
                                <div className="text-xs text-muted-foreground">{s.student_name} · {new Date(s.submitted_at).toLocaleString()}</div>
                                {s.is_late && <Badge className="bg-[#E11D48] hover:bg-[#E11D48] text-[10px] mt-1.5">LATE</Badge>}
                            </div>
                            <Button variant="outline" size="sm" onClick={() => download(s)} className="rounded-xl" data-testid={`download-sub-${s.id}`}><Download className="h-4 w-4 mr-2" /> Download</Button>
                            <Button size="sm" className="mx-btn-primary" onClick={() => { setGrading(s); setForm({ score: 80, remarks: "", approved: true }); }} data-testid={`grade-sub-${s.id}`}>Grade</Button>
                        </CardContent>
                    </Card>
                ))}
                {pending.length === 0 && <div className="mx-card p-12 text-center text-muted-foreground">All caught up 🎉</div>}
            </div>

            <Dialog open={!!grading} onOpenChange={(v) => !v && setGrading(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Grade submission</DialogTitle></DialogHeader>
                    {grading && (
                        <div className="space-y-3">
                            <div className="text-sm"><strong>{grading.assignment_title}</strong> · {grading.student_name}</div>
                            <div><Label>Score</Label><Input type="number" value={form.score} onChange={e => setForm({ ...form, score: +e.target.value })} data-testid="grade-score" /></div>
                            <div><Label>Remarks</Label><Textarea value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} data-testid="grade-remarks" /></div>
                            <div className="flex items-center gap-2"><input type="checkbox" checked={form.approved} onChange={(e) => setForm({ ...form, approved: e.target.checked })} className="accent-[#16A34A]" /><Label className="!mt-0">Mark as approved</Label></div>
                            <DialogFooter><Button onClick={grade} className="mx-btn-primary" data-testid="grade-submit">Save grade</Button></DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
